import React, { useState, useEffect } from 'react';
import { Joyride, STATUS } from 'react-joyride';
import { useTheme } from '../context/SocketContext';

const TourGuide = ({ run, setRun, onFinish }) => {
  const { isDarkMode, currentThemeColors } = useTheme();
  const [user, setUser] = useState(null);

  useEffect(() => {
    const fetchUserData = async () => {
      const token = localStorage.getItem("token");
      if (!token) return;

      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/user`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.ok) {
          const data = await response.json();
          setUser(data.user);
        }
      } catch (error) {
        console.error("User fetch error in TourGuide:", error);
      }
    };

    fetchUserData();
  }, []);

  // Disable tour guide for mobile users
  useEffect(() => {
    const checkMobile = () => {
      if (window.innerWidth <= 1024 && run) {
        setRun(false);
      }
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [run, setRun]);

  const steps = [
    {
      target: '#step-card-1',
      content: (
        <div className="text-left">
          <h3 className="font-bold text-lg mb-2">Step 1: Initial Information 📂</h3>
          <p>Start here by uploading your tax documents and answering technical questions. Once you submit, our team begins reviewing your folder.</p>
        </div>
      ),
      disableBeacon: true,
      placement: 'bottom',
    },
    {
      target: '#chat-button-tour',
      content: (
        <div className="text-left">
          <h3 className="font-bold text-lg mb-2">Need Help? Chat with Us! 💬</h3>
          <p>Have questions? Click the "Chat with Docs" button anytime to speak directly with your assigned tax professional.</p>
        </div>
      ),
      disableBeacon: true,
      placement: 'left',
    },
    {
      target: '#sidebar-documents',
      content: (
        <div className="text-left">
          <h3 className="font-bold text-lg mb-2">Access Your Documents 📁</h3>
          <p>You can view and download all your uploaded tax documents and signed returns anytime from this section.</p>
        </div>
      ),
      disableBeacon: true,
      placement: 'right',
    },
    {
      target: '#step-card-2',
      content: (
        <div className="text-left">
          <h3 className="font-bold text-lg mb-2">Step 2: Return Preparation ⚙️</h3>
          <p>While this is active, our dedicated tax specialists are working on your return. You'll see "Working for Employee" as the status.</p>
        </div>
      ),
      disableBeacon: true,
      placement: 'bottom',
    },
    {
      target: '#step-card-3',
      content: (
        <div className="text-left">
          <h3 className="font-bold text-lg mb-2">Step 3: Review Draft 🔍</h3>
          <p>Review the draft tax return prepared by our team. You can provide feedback or approve it if everything looks correct.</p>
        </div>
      ),
      disableBeacon: true,
      placement: 'bottom',
    },
    {
      target: '#step-card-4',
      content: (
        <div className="text-left">
          <h3 className="font-bold text-lg mb-2">Step 4: Return Filed ✅</h3>
          <p>After your final approval and signing, we'll file the return with the IRS and share the official acknowledgement here.</p>
        </div>
      ),
      disableBeacon: true,
      placement: 'bottom',
    },
    {
      target: '#sidebar-account-settings',
      content: (
        <div className="text-left">
          <h3 className="font-bold text-lg mb-2">Account Settings ⚙️</h3>
          <p>This is where you can manage your personal details and view your financial records.</p>
        </div>
      ),
      disableBeacon: true,
      placement: 'right',
    },
    {
      target: '#sidebar-pricing',
      content: (
        <div className="text-left">
          <h3 className="font-bold text-lg mb-2">Pricing Plan 💰</h3>
          <p>Check the available pricing plans and services included in your current subscription.</p>
        </div>
      ),
      disableBeacon: true,
      placement: 'right',
    },
    {
      target: '#sidebar-meetings',
      content: (
        <div className="text-left">
          <h3 className="font-bold text-lg mb-2">Meetings 🗓️</h3>
          <p>View and manage your upcoming scheduled meetings with our tax experts.</p>
        </div>
      ),
      disableBeacon: true,
      placement: 'right',
    },
    {
      target: '#sidebar-services-header',
      content: (
        <div className="text-left">
          <h3 className="font-bold text-lg mb-2">Our Services 🚀</h3>
          <p>Explore specialized financial and compliance services. If a service shows a lock icon, you can unlock it by upgrading your plan.</p>
        </div>
      ),
      disableBeacon: true,
      placement: 'right',
    },
    {
      target: '#sidebar-tax-filing',
      content: (
        <div className="text-left">
          <h3 className="font-bold text-lg mb-2">Tax Filing 📝</h3>
          <p>
            {user?.isTaxFilingEnabled
              ? "Comprehensive tax filing support for individuals and businesses."
              : "This service is locked. Upgrade your plan in 'Pricing Plan' to unlock expert tax filing support."}
          </p>
        </div>
      ),
      disableBeacon: true,
      placement: 'right',
    },
    {
      target: '#sidebar-bookkeeping',
      content: (
        <div className="text-left">
          <h3 className="font-bold text-lg mb-2">Bookkeeping 📚</h3>
          <p>
            {user?.isBookkeepingEnabled
              ? "Professional bookkeeping to keep your financial records accurate and up-to-date."
              : "Keep your books in order! Upgrade your plan to unlock our dedicated bookkeeping services."}
          </p>
        </div>
      ),
      disableBeacon: true,
      placement: 'right',
    },
    {
      target: '#sidebar-usa-company',
      content: (
        <div className="text-left">
          <h3 className="font-bold text-lg mb-2">USA Company Formation 🏢</h3>
          <p>
            {user?.isUSAEnabled
              ? "Expert guidance for setting up and managing your business entity in the United States."
              : "Ready to expand? Upgrade your plan to unlock USA Company Formation and compliance services."}
          </p>
        </div>
      ),
      disableBeacon: true,
      placement: 'right',
    },
    {
      target: '#sidebar-cfo-advisory',
      content: (
        <div className="text-left">
          <h3 className="font-bold text-lg mb-2">CFO Advisory 📈</h3>
          <p>
            {user?.isCFOEnabled
              ? "Strategic financial planning and advisory services to help your business scale."
              : "Strategic insights for your business. Upgrade to unlock expert CFO Advisory services."}
          </p>
        </div>
      ),
      disableBeacon: true,
      placement: 'right',
    },
    {
      target: '#sidebar-registered-agent',
      content: (
        <div className="text-left">
          <h3 className="font-bold text-lg mb-2">Registered Agent 🛡️</h3>
          <p>
            {user?.isRegisteredAgentEnabled
              ? "Reliable registered agent services to handle your official legal and tax documents."
              : "Ensure compliance with a Registered Agent. Upgrade your plan to unlock this service."}
          </p>
        </div>
      ),
      disableBeacon: true,
      placement: 'right',
    },
    {
      target: '#sidebar-annual-report',
      content: (
        <div className="text-left">
          <h3 className="font-bold text-lg mb-2">Annual Report Service 🗓️</h3>
          <p>
            {user?.isAnnualReportServiceEnabled
              ? "Timely filing of your company's annual reports to maintain good standing."
              : "Never miss a deadline! Upgrade your plan to unlock automated Annual Report filing."}
          </p>
        </div>
      ),
      disableBeacon: true,
      placement: 'right',
    },
  ];

  const handleJoyrideCallback = (data) => {
    const { status, type, index } = data;
    const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];

    if (finishedStatuses.includes(status)) {
      setRun(false);
      if (onFinish) onFinish();
    }

    // Automatically open sidebar dropdown for account settings steps
    if (type === 'step:before' || type === 'tooltip') {
      const target = data.step.target;
      if (target === '#sidebar-invoice' || target === '#sidebar-payment' || target === '#sidebar-profile') {
        if (typeof window.openAccountDropdown === 'function') {
          window.openAccountDropdown();
        }
      }
    }
  };

  return (
    <Joyride
      steps={steps}
      run={run}
      continuous={true}
      disableBeacon={true}
      showProgress={false}
      showSkipButton={true}
      callback={handleJoyrideCallback}
      locale={{
        last: "Finish Guide",
        skip: "Skip Tour",
      }}
      styles={{
        options: {
          arrowColor: isDarkMode ? '#1e293b' : '#fff',
          backgroundColor: isDarkMode ? '#1e293b' : '#fff',
          overlayColor: 'rgba(0, 0, 0, 0.6)',
          primaryColor: currentThemeColors?.primary || '#3b82f6',
          textColor: isDarkMode ? '#f1f5f9' : '#1e293b',
          zIndex: 1000,
        },
        tooltip: {
          borderRadius: '16px',
          padding: '24px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
        },
        tooltipContainer: {
          textAlign: 'left',
        },
        buttonNext: {
          backgroundColor: currentThemeColors?.primary || '#3b82f6',
          borderRadius: '12px',
          padding: '10px 20px',
          fontWeight: 'bold',
        },
        buttonBack: {
          marginRight: 10,
          color: isDarkMode ? '#94a3b8' : '#64748b',
          fontWeight: 'bold',
        },
        buttonSkip: {
          color: isDarkMode ? '#94a3b8' : '#64748b',
        },
      }}
    />
  );
};

export default TourGuide;
